import { api } from "@/lib/api";
import { ApiResponse } from "@/interfaces/api.interface";
import { BaseService } from "./base.service";
import { store } from "@/store/store";
import { hideLoader, showLoader } from "@/store/slices/loaderSlice";

export interface IReportView {
  id: string;
  name: string;
  projectId: string;
  filterState: string | null;
  sortState: string | null;
  columnState: string | null;
  isDefault: boolean;
  isDeleted: boolean;
  createdOn: string;
  modifiedOn: string;
}

export interface ICreateReportView {
  name: string;
  projectId: string;
  filterState?: string | null;
  sortState?: string | null;
  columnState?: string | null;
  isDefault?: boolean;
}

export interface IUpdateReportView {
  filterState?: string | null;
  sortState?: string | null;
  columnState?: string | null;
}

export interface IRenameReportView {
  name: string;
}

class ReportViewService extends BaseService {
  constructor() {
    super("ReportView");
  }

  async getViews(projectId: string): Promise<ApiResponse<IReportView[]>> {
    try {
      store.dispatch(showLoader())
      const res: any = await api.get(
        `${process.env.REACT_APP_BASE_URL}/api/ReportView?projectId=${projectId}`
      );
      store.dispatch(hideLoader())

      return {
        data: res.data,
        total: res.data.length,
        error: null,
      };
    } catch (error) {
      store.dispatch(hideLoader())

      console.error("Error fetching report views:", error);
      return {
        data: [],
        total: 0,
        error: error as any,
      };
    }
  }

  async getViewById(id: string): Promise<ApiResponse<IReportView>> {
    try {
      store.dispatch(showLoader())

      const res: any = await api.get(
        `${process.env.REACT_APP_BASE_URL}/api/ReportView/${id}`
      );
      store.dispatch(hideLoader())

      return {
        data: res.data,
        error: null,
      };
    } catch (error) {
      store.dispatch(hideLoader())

      console.error("Error fetching report view:", error);
      return {
        data: null as any,
        error: error as any,
      };
    }
  }

  async createView(createDto: ICreateReportView): Promise<ApiResponse<IReportView>> {
    try {
      store.dispatch(showLoader())

      const res: any = await api.post(
        `${process.env.REACT_APP_BASE_URL}/api/ReportView`,
        createDto
      );
      store.dispatch(hideLoader())

      return {
        data: res.data,
        error: null,
      };
    } catch (error) {
      store.dispatch(hideLoader())

      console.error("Error creating report view:", error);
      return {
        data: null as any,
        error: error as any,
      };
    }
  }

  async updateView(id: string, updateDto: IUpdateReportView): Promise<ApiResponse<boolean>> {
    try {
      store.dispatch(showLoader())

      await api.put(
        `${process.env.REACT_APP_BASE_URL}/api/ReportView/${id}`,
        updateDto
      );
      store.dispatch(hideLoader())

      return {
        data: true,
        error: null,
      };
    } catch (error) {
      store.dispatch(hideLoader())

      console.error("Error updating report view:", error);
      return {
        data: false,
        error: error as any,
      };
    }
  }

  async renameView(id: string, name: string): Promise<ApiResponse<boolean>> {
    try {
      store.dispatch(showLoader())

      await api.put(
        `${process.env.REACT_APP_BASE_URL}/api/ReportView/${id}/rename`,
        { name }
      );
      store.dispatch(hideLoader())

      return {
        data: true,
        error: null,
      };
    } catch (error) {
      store.dispatch(hideLoader())

      console.error("Error renaming report view:", error);
      return {
        data: false,
        error: error as any,
      };
    }
  }

  async deleteView(id: string): Promise<ApiResponse<boolean>> {
    try {
      store.dispatch(showLoader())

      await api.delete(
        `${process.env.REACT_APP_BASE_URL}/api/ReportView/${id}`
      );
      store.dispatch(hideLoader())

      return {
        data: true,
        error: null,
      };
    } catch (error) {
      store.dispatch(hideLoader())

      console.error("Error deleting report view:", error);
      return {
        data: false,
        error: error as any,
      };
    }
  }
}

const reportViewService = new ReportViewService();
export default reportViewService;
